import { useEffect, useRef, useState } from 'react';
import Pagination from '../Pagination/Pagination';
import {
  AutoparkOperationType,
  FinanceService,
  IAutoparkOperation,
  IOperationsFilters,
} from '../../services/finance.service';
import { formatMoney, toKopecks } from '../../utils/money';
import { NO_DATE, formatBackendDateTime } from '../../utils/date.utils';
import { clearSettledPayoutAttempts } from '../../utils/payoutAttempts';

interface AutoparkOperationsProps {
  autoparkId: string;
  dateFrom?: string;
  dateTo?: string;
  // Меняется, когда историю нужно перезагрузить (например, после выплаты)
  refreshKey: number;
  onBalanceLoaded: (balance: string) => void;
}

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<AutoparkOperationType, string> = {
  opening: 'Начальный остаток',
  accrual: 'Начисление',
  reversal: 'Сторно',
  payout: 'Выплата',
};

function formatSignedAmount(amount: string): string {
  const kopecks = toKopecks(amount);
  const sign = kopecks > 0 ? '+' : kopecks < 0 ? '−' : '';
  return `${sign}${formatMoney(Math.abs(kopecks) / 100)} Р`;
}

function operationDetails(operation: IAutoparkOperation): string {
  switch (operation.type) {
    case 'accrual':
    case 'reversal':
      return operation.roadlistNum !== null ? `ПЛ № ${operation.roadlistNum}` : '-';
    case 'payout':
      return operation.note || '-';
    default:
      return '-';
  }
}

function AutoparkOperations({ autoparkId, dateFrom, dateTo, refreshKey, onBalanceLoaded }: AutoparkOperationsProps) {
  const [operations, setOperations] = useState<IAutoparkOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Номер последнего запроса: ответ на устаревший запрос не должен перетереть список и баланс
  const requestRef = useRef(0);

  const loadOperations = async (page: number) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);

    try {
      const filters: IOperationsFilters = { page, pageSize: PAGE_SIZE, dateFrom, dateTo };
      const response = await FinanceService.getOperations(autoparkId, filters);
      if (requestId !== requestRef.current) return;

      setOperations(response.data);
      setCurrentPage(response.pagination.page);
      setTotalPages(response.pagination.totalPages);
      onBalanceLoaded(response.autoparkBalance);

      // Выплаты, которые уже видны в истории, больше не нужно повторять
      clearSettledPayoutAttempts(response.data.flatMap((op) => (op.payoutUuid ? [op.payoutUuid] : [])));
    } catch (err: any) {
      if (requestId === requestRef.current) setError(err.message);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadOperations(1);
  }, [autoparkId, dateFrom, dateTo, refreshKey]);

  return (
    <div className="operations-section">
      <h3 className="operations-section__title">История операций</h3>

      {loading ? (
        <div className="loading-state">Загрузка истории операций...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : operations.length === 0 ? (
        <div className="empty-state">Операций нет</div>
      ) : (
        <div className="finance-table">
          {operations.map((operation) => (
            <div key={operation.uuid} className="operations-row">
              <div className="finance-table-row__block">
                <div className="finance-table-row__label">Дата:</div>
                <div className="finance-table-row__value">{formatBackendDateTime(operation.createdAt, NO_DATE)}</div>
              </div>
              <div className="finance-table-row__block">
                <div className="finance-table-row__label">Операция:</div>
                <div className="finance-table-row__value">{TYPE_LABELS[operation.type] ?? operation.type}</div>
              </div>
              <div className="finance-table-row__block finance-table-row__block--amount">
                <div className="finance-table-row__label">Сумма:</div>
                <div className="finance-table-row__value">{formatSignedAmount(operation.amount)}</div>
              </div>
              <div className="finance-table-row__block finance-table-row__block--note">
                <div className="finance-table-row__label">Подробности:</div>
                <div className="finance-table-row__value">{operationDetails(operation)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="finance-pagination">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={loadOperations} />
        </div>
      )}
    </div>
  );
}

export default AutoparkOperations;
